import React, { useCallback, useEffect, useState } from 'react';
import {
  Users, TrendingUp, Clock, AlertTriangle, Search,
  Filter, MoreVertical, ChevronLeft, ChevronRight,
  CheckCircle2, CreditCard, RefreshCw,
} from 'lucide-react';
import { useApi } from '../../lib/api';

const PAGE_SIZE = 10;

function DonutChart({ slices, size = 80 }) {
  const total = slices.reduce((s, sl) => s + sl.value, 0);
  if (total === 0) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={size/2 - 8} fill="none" stroke="#e5e7eb" strokeWidth={12} />
      </svg>
    );
  }
  const r = size / 2 - 8;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  const arcs = slices.map((sl) => {
    const frac = sl.value / total;
    const dash = frac * circ;
    const arc = { ...sl, dash, offset };
    offset += dash;
    return arc;
  });
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
      {arcs.map((arc, i) => (
        <circle key={i} cx={size/2} cy={size/2} r={r}
          fill="none" stroke={arc.color} strokeWidth={12}
          strokeDasharray={`${arc.dash} ${circ - arc.dash}`}
          strokeDashoffset={-arc.offset} />
      ))}
    </svg>
  );
}

export default function TenantReports() {
  const api = useApi();
  const [search, setSearch]         = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tenants, setTenants]       = useState([]);
  const [overLimit, setOverLimit]   = useState([]);
  const [issues, setIssues]         = useState([]);
  const [error, setError]           = useState(null);
  const [loading, setLoading]       = useState(false);
  const [page, setPage]             = useState(1);
  const [openMenu, setOpenMenu]     = useState(null);
  const [menuPos, setMenuPos]       = useState({ top: 0, right: 0 });
  const [busy, setBusy]             = useState(null);
  const [confirm, setConfirm]       = useState(null);
  const [suspendReason, setSuspendReason] = useState('manual');
  const [showOverLimitOnly, setShowOverLimitOnly] = useState(false);
  const [plans, setPlans]           = useState([]);
  const [editPlanFor, setEditPlanFor] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [memberCount, setMemberCount] = useState('');
  const [planBusy, setPlanBusy]     = useState(false);
  const [planError, setPlanError]   = useState(null);
  const [blockModalFor, setBlockModalFor] = useState(null); // { tenant, willBlock }
  const [blockReason, setBlockReason] = useState('');
  const [blockBusy, setBlockBusy]   = useState(false);
  const [graceEditFor, setGraceEditFor] = useState(null);
  const [graceDays, setGraceDays]   = useState('');
  const [graceBusy, setGraceBusy]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, ol, pi] = await Promise.all([
        api.adminTenants(),
        api.adminTenantsOver(),
        api.adminTenantsPayIssues(),
      ]);
      setTenants(t.tenants || []);
      setOverLimit(ol.tenants || []);
      setIssues(pi.tenants || []);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.adminListPlans().then((res) => setPlans(res.plans || [])).catch(() => {});
  }, [api]);

  const openEditPlan = (t) => {
    setEditPlanFor(t);
    setSelectedPlanId(t.plan_id != null ? String(t.plan_id) : '');
    setMemberCount(t.purchased_seats != null ? String(t.purchased_seats) : '');
    setPlanError(null);
    setOpenMenu(null);
  };

  const saveEditPlan = async () => {
    if (!editPlanFor || !selectedPlanId) return;
    setPlanBusy(true);
    setPlanError(null);
    try {
      await api.adminChangePlan(editPlanFor.id, {
        plan_id: Number(selectedPlanId),
        member_count: memberCount === '' ? null : Number(memberCount),
      });
      const plan = plans.find((p) => p.id === Number(selectedPlanId));
      const isFreePlan = plan?.pricePaise === 0;
      setTenants((prev) => prev.map((a) => a.id === editPlanFor.id
        ? {
            ...a,
            plan_id: Number(selectedPlanId),
            plan_code: plan?.code,
            max_users: memberCount === '' ? (isFreePlan ? (plan?.maxUsers ?? null) : null) : Number(memberCount),
            purchased_seats: memberCount === '' ? null : Number(memberCount),
          }
        : a));
      setEditPlanFor(null);
    } catch (err) {
      setPlanError(err?.response?.data?.error || err.message);
    } finally {
      setPlanBusy(false);
    }
  };

  useEffect(() => {
    const h = () => setOpenMenu(null);
    document.addEventListener('click', h);
    // The row menu is position:fixed (so the scrolling table can't clip it),
    // so close it rather than let it drift away from its button.
    window.addEventListener('scroll', h, true);
    window.addEventListener('resize', h);
    return () => {
      document.removeEventListener('click', h);
      window.removeEventListener('scroll', h, true);
      window.removeEventListener('resize', h);
    };
  }, []);

  const applyStatus = async (appId, status) => {
    setBusy(appId);
    try {
      const body = { status };
      if (status === 'suspended') body.suspension_reason = suspendReason;
      await api.adminUpdateAppStatus(appId, body);
      setTenants((prev) => prev.map((a) => a.id === appId ? { ...a, billing_status: status } : a));
    } catch (err) {
      alert(err?.response?.data?.error || err.message);
    } finally {
      setBusy(null);
      setConfirm(null);
      setSuspendReason('manual');
    }
  };

  // Manual block is independent of billing_status — see
  // calculateEffectiveAccess in usageLimit.js. Toggling it here never
  // touches billing_status, and clearing it never assumes the tenant
  // should become active (the backend recalculates access from scratch).
  const openBlockModal = (t) => {
    setBlockModalFor({ tenant: t, willBlock: !t.manual_block });
    setBlockReason('');
    setOpenMenu(null);
  };

  const applyBlock = async () => {
    if (!blockModalFor) return;
    const { tenant, willBlock } = blockModalFor;
    setBlockBusy(true);
    try {
      const body = { manual_block: willBlock };
      if (willBlock) body.manual_block_reason = blockReason || null;
      await api.adminUpdateAppStatus(tenant.id, body);
      setTenants((prev) => prev.map((a) => a.id === tenant.id
        ? { ...a, manual_block: willBlock, manual_block_reason: willBlock ? (blockReason || null) : null }
        : a));
      setBlockModalFor(null);
    } catch (err) {
      alert(err?.response?.data?.error || err.message);
    } finally {
      setBlockBusy(false);
    }
  };

  const openGraceEdit = (t) => {
    setGraceEditFor(t);
    setGraceDays(String(t.grace_period_days ?? 0));
    setOpenMenu(null);
  };

  const saveGrace = async () => {
    if (!graceEditFor) return;
    const days = Number(graceDays);
    if (!Number.isInteger(days) || days < 0) {
      alert('Grace period must be a non-negative whole number of days');
      return;
    }
    setGraceBusy(true);
    try {
      await api.adminUpdateAppStatus(graceEditFor.id, { grace_period_days: days });
      setTenants((prev) => prev.map((a) => a.id === graceEditFor.id ? { ...a, grace_period_days: days } : a));
      setGraceEditFor(null);
    } catch (err) {
      alert(err?.response?.data?.error || err.message);
    } finally {
      setGraceBusy(false);
    }
  };

  // KPI counts
  const total  = tenants.length;
  const active = tenants.filter(t => t.billing_status === 'active').length;
  const trial  = tenants.filter(t => t.billing_status === 'trial').length;
  const over   = overLimit.length;

  // Filter & paginate
  const overLimitIds = new Set(overLimit.map(t => t.id));
  const filtered = tenants.filter(t => {
    const matchSearch = !search ||
      t.name?.toLowerCase().includes(search.toLowerCase()) ||
      t.owner_email?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !statusFilter || t.billing_status === statusFilter;
    const matchOverLimit = !showOverLimitOnly || overLimitIds.has(t.id);
    return matchSearch && matchStatus && matchOverLimit;
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Plan distribution
  const planMap = {};
  for (const t of tenants) {
    const key = t.billing_status || 'unknown';
    planMap[key] = (planMap[key] || 0) + 1;
  }
  const planColors = { trial: '#a855f7', active: '#3b82f6', expired: '#22c55e', suspended: '#ef4444', cancelled: '#9ca3af' };
  const planSlices = Object.entries(planMap).map(([k, v]) => ({ label: k, value: v, color: planColors[k] || '#d1d5db' }));

  const pageNums = buildPageNums(page, totalPages);

  if (error) return <div className="admin-banner error">{error}</div>;

  return (
    <>
      {/* Page header */}
      <div className="admin-main-header">
        <div>
          <h1 className="admin-page-title">Tenants</h1>
          <p className="admin-page-subtitle">All apps, plan distribution, and compliance flags.</p>
        </div>
        <div className="admin-header-actions">
          <button className="btn-secondary" onClick={load} disabled={loading} style={{ gap: 6 }}>
            <RefreshCw size={13} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="admin-kpi-row" style={{ marginBottom: 20 }}>
        <div className="admin-kpi">
          <div className="admin-kpi-icon blue"><Users size={18} /></div>
          <p className="admin-kpi-label">Total Tenants</p>
          <p className="admin-kpi-value">{total}</p>
          <p className="admin-kpi-sub">{total} registered apps</p>
        </div>
        <div className="admin-kpi ok">
          <div className="admin-kpi-icon green"><TrendingUp size={18} /></div>
          <p className="admin-kpi-label">Active Tenants</p>
          <p className="admin-kpi-value">{active}</p>
          <p className="admin-kpi-sub">{total ? Math.round(active / total * 100) : 0}% of total</p>
        </div>
        <div className="admin-kpi">
          <div className="admin-kpi-icon purple"><Clock size={18} /></div>
          <p className="admin-kpi-label">Trial Tenants</p>
          <p className="admin-kpi-value">{trial}</p>
          <p className="admin-kpi-sub">{total ? Math.round(trial / total * 100) : 0}% of total</p>
        </div>
        <div
          className={`admin-kpi ${over > 0 ? 'warn' : ''}`}
          onClick={() => { setShowOverLimitOnly(v => !v); setPage(1); }}
          style={{ cursor: over > 0 ? 'pointer' : 'default', outline: showOverLimitOnly ? '2px solid #d97706' : 'none', borderRadius: 10 }}
          title={over > 0 ? (showOverLimitOnly ? 'Click to show all tenants' : 'Click to filter over-limit tenants') : undefined}
        >
          <div className={`admin-kpi-icon ${over > 0 ? 'orange' : 'purple'}`}><AlertTriangle size={18} /></div>
          <p className="admin-kpi-label">Over Limit {showOverLimitOnly && <span style={{ fontSize: 10, background: '#d97706', color: 'white', borderRadius: 4, padding: '1px 5px', marginLeft: 4 }}>Filtered</span>}</p>
          <p className="admin-kpi-value">{over}</p>
          <p className="admin-kpi-sub">{over > 0 ? (showOverLimitOnly ? 'Click to clear filter' : 'Click to view') : `${total ? Math.round(over / total * 100) : 0}% of total`}</p>
        </div>
      </div>

      {/* Tenants table */}
      <div className="admin-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 10, flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0 }}>All Tenants ({filtered.length})</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ position: 'relative' }}>
              <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
              <input
                placeholder="Search tenants..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                autoComplete="off"
                style={{ font: 'inherit', fontSize: 13, padding: '6px 12px 6px 30px', border: '1px solid #d1d5db', borderRadius: 8, width: 200, outline: 'none', color: '#374151' }}
              />
            </div>
            <button style={{ display: 'flex', alignItems: 'center', gap: 6, font: 'inherit', fontSize: 13, padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 8, background: 'white', cursor: 'pointer', color: '#374151' }}>
              <Filter size={13} /> Filter
            </button>
            <select
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
              style={{ font: 'inherit', fontSize: 13, padding: '6px 28px 6px 10px', border: '1px solid #d1d5db', borderRadius: 8, appearance: 'none', background: `white url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%239ca3af' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat right 8px center`, color: '#374151', cursor: 'pointer' }}
            >
              <option value="">Any Status</option>
              <option value="trial">Trial</option>
              <option value="active">Active</option>
              <option value="past_due">Past Due</option>
              <option value="suspended">Suspended</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="admin-empty">No tenants match.</div>
        ) : (
          <>
            <div className="admin-table-hscroll">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>↑ APP</th>
                  <th>PLAN</th>
                  <th>STATUS ↕</th>
                  <th>ACCESS</th>
                  <th>MEMBERS</th>
                  <th>LIMIT</th>
                  <th>OWNER</th>
                  <th>CREATED ↕</th>
                  <th>LAST LOGIN</th>
                  <th>ACTIONS</th>
                  <th style={{ width: 32 }}></th>
                </tr>
              </thead>
              <tbody>
                {paged.map((t) => {
                  const effectiveLimit = t.max_users; // already the correct cap from the backend (Free plan only)
                  return (
                  <tr key={t.id}>
                    <td style={{ fontWeight: 600, color: '#0f1c2e' }}>{t.name}</td>
                    <td style={{ color: t.plan_code ? '#374151' : '#d1d5db' }}>{t.plan_code || '—'}</td>
                    <td><span className={`admin-status-pill ${pillFor(t.billing_status)}`}>{fmt(t.billing_status)}</span></td>
                    <td>
                      <span className={`admin-status-pill ${accessPillFor(t.access_state)}`} title={t.access_reason || ''}>
                        {accessLabel(t.access_state)}
                      </span>
                      {t.access_state === 'GRACE' && t.grace_expires_at && (
                        <div style={{ fontSize: 10, color: '#d97706', marginTop: 2 }}>
                          until {(t.grace_expires_at || '').slice(0, 10)}
                        </div>
                      )}
                    </td>
                    <td>
                      {t.member_count}
                      {effectiveLimit != null && t.member_count > effectiveLimit && (
                        <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: '#d97706', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 4, padding: '1px 5px' }}>
                          Over Limit
                        </span>
                      )}
                    </td>
                    <td>{effectiveLimit == null ? '∞' : effectiveLimit}</td>
                    <td style={{ fontSize: 12, color: '#6b7280' }}>{t.owner_email || '—'}</td>
                    <td style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{(t.created_at || '').slice(0, 10).split('-').reverse().join('-')}</td>
                    <td style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
                      {fmtLastLogin(t.last_login_at)}
                      {t.last_login_at && <div style={{ fontSize: 11, color: '#9ca3af' }}>{t.last_login_is_owner ? 'Owner' : 'Member'}</div>}
                    </td>
                    <td>
                      {t.billing_status === 'suspended' ? (
                        <button
                          className="btn-secondary"
                          style={{ color: '#16a34a', borderColor: '#bbf7d0', fontSize: 12, padding: '3px 10px' }}
                          disabled={busy === t.id}
                          onClick={() => setConfirm({ appId: t.id, appName: t.name, action: 'Activate', status: 'active' })}
                        >
                          Activate
                        </button>
                      ) : (
                        <button
                          className="btn-secondary"
                          style={{ color: '#d97706', borderColor: '#fde68a', fontSize: 12, padding: '3px 10px' }}
                          disabled={busy === t.id}
                          onClick={() => {
                            const autoReason = (effectiveLimit != null && t.member_count > effectiveLimit) ? 'over_limit' : 'manual';
                            setSuspendReason(autoReason);
                            setConfirm({ appId: t.id, appName: t.name, action: 'Deactivate', status: 'suspended' });
                          }}
                        >
                          Deactivate
                        </button>
                      )}
                    </td>
                    <td>
                      <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
                        <button
                          onClick={(e) => {
                            const r = e.currentTarget.getBoundingClientRect();
                            setMenuPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
                            setOpenMenu(openMenu === t.id ? null : t.id);
                          }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 6, color: '#9ca3af' }}
                        >
                          <MoreVertical size={15} />
                        </button>
                        {openMenu === t.id && (
                          <div style={{ position: 'fixed', right: menuPos.right, top: menuPos.top, zIndex: 50, background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.1)', minWidth: 140, overflow: 'hidden' }}>
                            <button onClick={() => openEditPlan(t)} style={menuBtnStyle}>Edit Plan</button>
                            <button onClick={() => openGraceEdit(t)} style={menuBtnStyle}>Grace Period{t.grace_period_days ? ` (${t.grace_period_days}d)` : ''}</button>
                            <button onClick={() => openBlockModal(t)} style={{ ...menuBtnStyle, color: t.manual_block ? '#16a34a' : '#dc2626' }}>
                              {t.manual_block ? 'Remove Manual Block' : 'Manual Block'}
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            </div>

            {/* Pagination */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, flexWrap: 'wrap', gap: 8 }}>
              <span style={{ fontSize: 12, color: '#6b7280' }}>
                Showing {Math.min((page - 1) * PAGE_SIZE + 1, filtered.length)} to {Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} tenants
              </span>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <select defaultValue="10" style={perPageStyle} disabled>
                  <option value="10">10 per page</option>
                </select>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={pageBtn(page === 1)}>
                  <ChevronLeft size={14} />
                </button>
                {pageNums.map((p, i) =>
                  p === '…'
                    ? <span key={`e-${i}`} style={{ padding: '0 4px', color: '#9ca3af', fontSize: 13 }}>…</span>
                    : <button key={p} onClick={() => setPage(p)} style={pageBtn(false, page === p)}>{p}</button>
                )}
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={pageBtn(page === totalPages)}>
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Bottom 3-col section */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* Over user limit */}
        <div className="admin-card" style={{ marginBottom: 0 }}>
          <h2 style={{ marginBottom: 16 }}>Over User Limit ({overLimit.length})</h2>
          {overLimit.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '8px 0' }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <CheckCircle2 size={20} color="#16a34a" />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#0f1c2e', marginBottom: 2 }}>All Good!</div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>No tenants are over their user limit.</div>
              </div>
            </div>
          ) : (
            <div className="admin-table-scroll"><table className="admin-table">
              <thead><tr><th>App</th><th>Members</th><th>Allowed</th></tr></thead>
              <tbody>
                {overLimit.map(t => (
                  <tr key={t.id}>
                    <td style={{ fontWeight: 600 }}>{t.name}</td>
                    <td style={{ color: '#d97706', fontWeight: 700 }}>{t.member_count}</td>
                    <td>{t.max_users}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </div>

        {/* Payment issues */}
        <div className="admin-card" style={{ marginBottom: 0 }}>
          <h2 style={{ marginBottom: 16 }}>Payment Issues ({issues.length})</h2>
          {issues.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '8px 0' }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <CreditCard size={20} color="#f97316" />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#0f1c2e', marginBottom: 2 }}>No Payment Issues</div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>No payment issues are currently open.</div>
              </div>
            </div>
          ) : (
            <div className="admin-table-scroll"><table className="admin-table">
              <thead><tr><th>App</th><th>Status</th><th>Issues</th></tr></thead>
              <tbody>
                {issues.map(t => (
                  <tr key={t.id}>
                    <td style={{ fontWeight: 600 }}>{t.name}</td>
                    <td><span className={`admin-status-pill ${pillFor(t.billing_status)}`}>{fmt(t.billing_status)}</span></td>
                    <td>{t.issue_count}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </div>

        {/* Plan distribution */}
        <div className="admin-card" style={{ marginBottom: 0 }}>
          <h2 style={{ marginBottom: 16 }}>Plan Distribution</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <DonutChart slices={planSlices} size={90} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
              {planSlices.map(sl => (
                <div key={sl.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: sl.color, flexShrink: 0 }} />
                    <span style={{ color: '#374151', textTransform: 'capitalize' }}>{sl.label}</span>
                  </div>
                  <span style={{ fontWeight: 700, color: '#0f1c2e' }}>
                    {sl.value} <span style={{ color: '#9ca3af', fontWeight: 400 }}>({total ? Math.round(sl.value / total * 100) : 0}%)</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {confirm && (
        <div className="modal-backdrop" onClick={() => setConfirm(null)}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h2>{confirm.action} Tenant</h2>
              <button className="modal-close" onClick={() => setConfirm(null)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ color: '#374151' }}>
                Are you sure you want to <strong>{confirm.action.toLowerCase()}</strong> <strong>{confirm.appName}</strong>?
              </p>
              {confirm.status === 'suspended' && (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                      Reason For Suspension
                    </label>
                    <select
                      value={suspendReason}
                      onChange={e => setSuspendReason(e.target.value)}
                      style={{ width: '100%', font: 'inherit', fontSize: 13, padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 8, color: '#374151' }}
                    >
                      <option value="over_limit">Exceeded User Limit</option>
                      <option value="payment_due">Payment Overdue</option>
                      <option value="manual">Other / Manual</option>
                    </select>
                  </div>
                  <p style={{ color: '#d97706', fontSize: 13, background: '#fff7ed', padding: '8px 12px', borderRadius: 8 }}>
                    The tenant's app will be inaccessible until reactivated.
                  </p>
                </>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                <button className="btn-secondary" onClick={() => setConfirm(null)}>Cancel</button>
                <button
                  className="btn-primary"
                  style={confirm.status === 'suspended' ? { background: '#d97706', borderColor: '#d97706' } : {}}
                  disabled={busy === confirm.appId}
                  onClick={() => applyStatus(confirm.appId, confirm.status)}
                >
                  {busy === confirm.appId ? 'Saving…' : confirm.action}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit plan modal */}
      {editPlanFor && (
        <div className="modal-backdrop" onClick={() => setEditPlanFor(null)}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h2>Edit Plan — {editPlanFor.name}</h2>
              <button className="modal-close" onClick={() => setEditPlanFor(null)}>×</button>
            </div>
            <div className="modal-body">
              {planError && <div className="profile-banner error" style={{ marginBottom: 12 }}>{planError}</div>}
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                Plan
              </label>
              <select
                value={selectedPlanId}
                onChange={(e) => setSelectedPlanId(e.target.value)}
                style={{ width: '100%', font: 'inherit', fontSize: 13, padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 8, color: '#374151' }}
              >
                <option value="" disabled>Select a plan…</option>
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} — {p.pricePaise === 0 ? 'Free' : `₹${(p.pricePaise / 100).toFixed(2)}/${p.interval}`}
                    {p.maxUsers != null ? ` (up to ${p.maxUsers} users)` : ' (unlimited users)'}
                  </option>
                ))}
              </select>

              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, marginTop: 14 }}>
                Member Limit (Optional)
              </label>
              <input
                type="number"
                min={1}
                placeholder="Leave blank to use the plan's default limit"
                value={memberCount}
                onChange={(e) => setMemberCount(e.target.value)}
                style={{ width: '100%', font: 'inherit', fontSize: 13, padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 8, color: '#374151' }}
              />

              <p style={{ color: '#6b7280', fontSize: 12, marginTop: 8 }}>
                This assigns the plan directly (no payment) and marks it as platform-admin-assigned.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                <button className="btn-secondary" onClick={() => setEditPlanFor(null)}>Cancel</button>
                <button className="btn-primary" disabled={planBusy || !selectedPlanId} onClick={saveEditPlan}>
                  {planBusy ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual block modal */}
      {blockModalFor && (
        <div className="modal-backdrop" onClick={() => setBlockModalFor(null)}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h2>
                {blockModalFor.willBlock ? 'Block' : 'Unblock'} {blockModalFor.tenant.name}
                {' — '}Manual Block
              </h2>
              <button className="modal-close" onClick={() => setBlockModalFor(null)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-primary)' }}>
                {blockModalFor.willBlock ? (
                  <>This is independent of billing status — the tenant will be blocked regardless of subscription state, and will remain blocked through any renewal until explicitly unblocked here.</>
                ) : (
                  <>This only clears the manual block. If the subscription is separately expired, the tenant may still be blocked for that reason.</>
                )}
              </p>
              {blockModalFor.willBlock && (
                <div style={{ marginTop: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>
                    Reason (optional)
                  </label>
                  <input
                    value={blockReason}
                    onChange={(e) => setBlockReason(e.target.value)}
                    placeholder="Why is this tenant being blocked?"
                    style={{ width: '100%', font: 'inherit', fontSize: 13, padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', background: 'var(--bg-input)' }}
                  />
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                <button className="btn-secondary" onClick={() => setBlockModalFor(null)}>Cancel</button>
                <button
                  className="btn-primary"
                  style={blockModalFor.willBlock ? { background: '#dc2626', borderColor: '#dc2626' } : {}}
                  disabled={blockBusy}
                  onClick={applyBlock}
                >
                  {blockBusy ? 'Saving…' : (blockModalFor.willBlock ? 'Block' : 'Unblock')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Grace period edit modal */}
      {graceEditFor && (
        <div className="modal-backdrop" onClick={() => setGraceEditFor(null)}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <div className="modal-header">
              <h2>Grace Period — {graceEditFor.name}</h2>
              <button className="modal-close" onClick={() => setGraceEditFor(null)}>×</button>
            </div>
            <div className="modal-body">
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>
                Days after subscription expiry before access is blocked
              </label>
              <input
                type="number"
                min={0}
                value={graceDays}
                onChange={(e) => setGraceDays(e.target.value)}
                style={{ width: '100%', font: 'inherit', fontSize: 13, padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', background: 'var(--bg-input)' }}
              />
              <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 8 }}>
                Default is 0 (no grace — access blocks immediately at expiry). This does not change the subscription's end date, only how long access continues past it.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                <button className="btn-secondary" onClick={() => setGraceEditFor(null)}>Cancel</button>
                <button className="btn-primary" disabled={graceBusy} onClick={saveGrace}>
                  {graceBusy ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}

/* ---- helpers ---- */
function pillFor(s) {
  if (s === 'active')    return 'ok';
  if (s === 'trial')     return 'running';
  if (s === 'past_due')  return 'partial';
  if (s === 'suspended') return 'failed';
  if (s === 'cancelled') return 'failed';
  return 'skipped';
}

function fmt(s) {
  return (s || '').split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || '—';
}

// Latest sign-in by anyone in the app, in the viewer's local time, e.g. "31-10-2026, 14:05".
function fmtLastLogin(iso) {
  if (!iso) return 'Never';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).replace(/\//g, '-');
}

// access_state is the centralized effective-access decision from
// calculateEffectiveAccess (usageLimit.js) — distinct from billing_status,
// since a tenant can be billing_status='active' yet still MANUAL_BLOCKED,
// or billing_status='suspended' yet still within GRACE.
function accessPillFor(s) {
  if (s === 'ACTIVE')            return 'ok';
  if (s === 'GRACE')             return 'partial';
  if (s === 'PAYMENT_SUSPENDED') return 'failed';
  if (s === 'MANUAL_BLOCKED')    return 'failed';
  return 'skipped';
}

function accessLabel(s) {
  if (s === 'ACTIVE')            return 'Active';
  if (s === 'GRACE')             return 'Grace Period';
  if (s === 'PAYMENT_SUSPENDED') return 'Payment Suspended';
  if (s === 'MANUAL_BLOCKED')    return 'Manual Block';
  return '—';
}

function buildPageNums(cur, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1, 2, 3];
  if (cur > 3 && cur < total - 1) { pages.push('…', cur); }
  pages.push('…', total);
  return [...new Set(pages)];
}

const menuBtnStyle = {
  display: 'block', width: '100%', padding: '8px 14px', background: 'none',
  border: 'none', textAlign: 'left', fontSize: 13, cursor: 'pointer', color: '#374151',
};

const perPageStyle = {
  font: 'inherit', fontSize: 13, padding: '5px 24px 5px 8px',
  border: '1px solid #e5e7eb', borderRadius: 6,
  appearance: 'none', background: `white url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%239ca3af' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat right 6px center`,
  color: '#374151', cursor: 'pointer',
};

function pageBtn(disabled, active) {
  return {
    width: 32, height: 32, border: '1px solid #e5e7eb', borderRadius: 6,
    background: active ? '#0f1c2e' : 'white',
    color: disabled ? '#d1d5db' : active ? 'white' : '#374151',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13, fontWeight: active ? 700 : 400,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
}
